import { useEffect, useMemo, useState } from 'react';
import { useGetLeaderboardQuery, useGetUserLeaderboardRankQuery } from '@/store/api';
import { skipToken } from '@reduxjs/toolkit/query';
import { MARKET_SECTIONS, REFETCH_LEADERBOARD_DELAY, TABS } from '@/constants';
import { useScheduledThrottle } from '../generic/use-scheduled-throttle.hook';

interface UseLobbyLeaderboardProps {
	slatesData: any[];
	slateFiltersState: any;
	eventId: string | undefined;
	lobbyId: string | undefined;
	lobbyResponse: any;
	isCrowdLockMode: boolean;
	user: any;
	isSlatesLoading: boolean;
	isAdmin: boolean;
	userTrades: any[];
	activeTab: string;
}

export function useLobbyLeaderboard({
	slatesData,
	slateFiltersState,
	eventId,
	lobbyId,
	lobbyResponse,
	isCrowdLockMode,
	user,
	isSlatesLoading,
	isAdmin,
	userTrades,
	activeTab,
}: UseLobbyLeaderboardProps) {
	const overAllMatches = useMemo(
		() => slatesData.flatMap((slate: any) => slate.matches?.map((m: any) => m.id) || []) || [],
		[slatesData]
	);

	const selectedMatches = useMemo(() => {
		const leaderboardTab = slateFiltersState.tabs[MARKET_SECTIONS.Leaderboard];
		if (!leaderboardTab) return [];

		const allSlates = leaderboardTab.all?.slates || [];
		const selectedSlateIds = leaderboardTab.selected?.slates || [];

		if (leaderboardTab.isOverall) {
			return overAllMatches;
		}

		if (leaderboardTab.selected?.matches?.length) {
			return leaderboardTab.selected.matches;
		}

		return allSlates
			.filter((slate: any) => selectedSlateIds.includes(slate.id))
			.reduce((acc: any[], { matches }: any) => {
				if (matches) acc.push(...matches.map((m: any) => m.id));
				return acc;
			}, []);
	}, [slateFiltersState.tabs, overAllMatches]);

	const isOverallLeaderboard = useMemo(() => {
		return slateFiltersState.tabs[MARKET_SECTIONS.Leaderboard]?.isOverall;
	}, [slateFiltersState.tabs]);

	const shouldFetchLeaderboard =
		eventId &&
		lobbyId &&
		!(!isOverallLeaderboard && (!selectedMatches || selectedMatches.length === 0)) &&
		lobbyResponse &&
		!isCrowdLockMode;

	const {
		data: leaderboardByMatch,
		refetch: refetchLeaderboard,
		isLoading: isLoadingLeaderboard,
	} = useGetLeaderboardQuery(
		shouldFetchLeaderboard
			? { eventId, lobbyId, matchIds: selectedMatches, isOverall: isOverallLeaderboard }
			: skipToken,
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const { data: userLeaderboardRank, refetch: refetchUserLeaderboardRank } =
		useGetUserLeaderboardRankQuery(
			user &&
				eventId &&
				lobbyId &&
				!isSlatesLoading &&
				!isAdmin &&
				userTrades?.length > 0 &&
				lobbyResponse &&
				!isCrowdLockMode
				? { eventId, lobbyId, matchIds: overAllMatches, isOverall: true }
				: skipToken,
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const leaderboard = shouldFetchLeaderboard ? leaderboardByMatch : [];

	const { schedule: scheduleRefetchLeaderboard, setRefetchFn: setLeaderboardRefetch } =
		useScheduledThrottle(REFETCH_LEADERBOARD_DELAY);

	const { schedule: scheduleRefetchUserLeaderboardRank, setRefetchFn: setUserRankRefetch } =
		useScheduledThrottle(REFETCH_LEADERBOARD_DELAY);

	useEffect(() => {
		setLeaderboardRefetch(refetchLeaderboard);
	}, [refetchLeaderboard, setLeaderboardRefetch]);

	useEffect(() => {
		setUserRankRefetch(refetchUserLeaderboardRank);
	}, [refetchUserLeaderboardRank, setUserRankRefetch]);

	const [shouldRefetchLeaderboard, setShouldRefetchLeaderboard] = useState(false);

	const handleRefetchLeaderboard = async () => {
		setShouldRefetchLeaderboard(true);
		if (!isAdmin) {
			refetchUserLeaderboardRank();
		}
	};

	useEffect(() => {
		if (activeTab !== TABS.LEADERBOARD || !shouldRefetchLeaderboard || !shouldFetchLeaderboard)
			return;

		refetchLeaderboard();

		const timer = setTimeout(() => {
			refetchLeaderboard();
			setShouldRefetchLeaderboard(false);
		}, REFETCH_LEADERBOARD_DELAY);

		return () => clearTimeout(timer);
	}, [activeTab, shouldRefetchLeaderboard, shouldFetchLeaderboard, refetchLeaderboard]);

	return {
		overAllMatches,
		selectedMatches,
		isOverallLeaderboard,
		shouldFetchLeaderboard,
		leaderboard,
		isLoadingLeaderboard,
		userLeaderboardRank,
		refetchUserLeaderboardRank,
		refetchLeaderboard,
		scheduleRefetchLeaderboard,
		scheduleRefetchUserLeaderboardRank,
		handleRefetchLeaderboard,
		setShouldRefetchLeaderboard,
	};
}
